import { z } from 'zod';
import { uuid, paginationQuery, isoDatetime } from './common.js';

// ─── Enum constant ─────────────────────────────────────────────────────────
export const AnnouncementCategory = {
  OUTAGE: 'OUTAGE',
  MAINTENANCE: 'MAINTENANCE',
  GENERAL: 'GENERAL',
  SECURITY: 'SECURITY',
} as const;
export type AnnouncementCategory =
  (typeof AnnouncementCategory)[keyof typeof AnnouncementCategory];

// Cover image: a data: URL (R2-absent dev fallback) OR a served /api/v1/files
// relative path. `.url()` would reject the relative path, so validate as a
// capped string instead (matches complaints `photoUrl`). Cap accommodates a
// ~2 MB image as base64 (~2.8 M chars) plus slack.
const coverImageUrl = z.string().min(1).max(2_900_000);

// ─── Public shape returned by GET endpoints ────────────────────────────────
export const publicAnnouncement = z.object({
  id: uuid,
  cluster_id: uuid,
  author_user_id: uuid,
  category: z.enum([
    AnnouncementCategory.OUTAGE,
    AnnouncementCategory.MAINTENANCE,
    AnnouncementCategory.GENERAL,
    AnnouncementCategory.SECURITY,
  ]),
  title: z.string(),
  body_md: z.string(),
  starts_at: isoDatetime.nullable(),
  ends_at: isoDatetime.nullable(),
  require_ack: z.boolean(),
  pinned: z.boolean(),
  cover_image_url: coverImageUrl.nullable(),
  published_at: isoDatetime.nullable(),
  created_at: isoDatetime,
  updated_at: isoDatetime,
  // Per-viewer state (only set when fetched by an authenticated resident)
  is_read: z.boolean().optional(),
  is_acked: z.boolean().optional(),
});
export type PublicAnnouncement = z.infer<typeof publicAnnouncement>;

// ─── Staff: create / update ────────────────────────────────────────────────
export const createAnnouncementRequest = z.object({
  category: z.enum([
    AnnouncementCategory.OUTAGE,
    AnnouncementCategory.MAINTENANCE,
    AnnouncementCategory.GENERAL,
    AnnouncementCategory.SECURITY,
  ]),
  title: z.string().min(1).max(200).trim(),
  body_md: z.string().min(1).max(20_000),
  starts_at: isoDatetime.optional(),
  ends_at: isoDatetime.optional(),
  require_ack: z.boolean().default(false),
  pinned: z.boolean().default(false),
  cover_image_url: coverImageUrl.optional(),
  publish_now: z.boolean().default(true),
});
export type CreateAnnouncementRequest = z.infer<typeof createAnnouncementRequest>;

export const updateAnnouncementRequest = createAnnouncementRequest.partial();
export type UpdateAnnouncementRequest = z.infer<typeof updateAnnouncementRequest>;

// ─── Resident: list query ──────────────────────────────────────────────────
export const listAnnouncementsQuery = paginationQuery.extend({
  category: z
    .enum([
      AnnouncementCategory.OUTAGE,
      AnnouncementCategory.MAINTENANCE,
      AnnouncementCategory.GENERAL,
      AnnouncementCategory.SECURITY,
    ])
    .optional(),
  pinned_only: z.coerce.boolean().optional(),
  since: isoDatetime.optional(),
});
export type ListAnnouncementsQuery = z.infer<typeof listAnnouncementsQuery>;
